// ============================================
// Partner Theme - Centralized Color System
// ============================================
// All colors used in the partner module should be defined here.
// Import and use these constants instead of hardcoding hex values.

// ============================================
// Raw Color Values (for JS usage, inline styles)
// ============================================
export const colors = {
	// Grays
	gray: {
		50: "#F9FAFB",
		100: "#F3F4F6",
		200: "#E5E7EB",
		300: "#D1D5DB",
		400: "#9CA3AF",
		500: "#6B7280",
		600: "#4B5563",
		700: "#374151",
		800: "#1F2A37",
		900: "#111928",
	},
	// Blues
	blue: {
		50: "#EBF5FF",
		100: "#E1EFFE",
		500: "#3F83F8",
		600: "#2563EB",
		700: "#1D4ED8",
	},
	// Greens
	green: {
		100: "#DEF7EC",
		500: "#0E9F6E",
		600: "#059669",
	},
	// Reds
	red: {
		100: "#FDE8E8",
		500: "#F05252",
		600: "#DC2626",
	},
	// Oranges
	orange: {
		100: "#FEECDC",
		500: "#F97316",
		600: "#EA580C",
	},
	// Purples
	purple: {
		100: "#EDEBFE",
		600: "#7C3AED",
	},
	// Coral (brand)
	coral: {
		500: "#EF4565",
		600: "#D93D5A",
	},
	// White
	white: "#FFFFFF",
} as const;

// ============================================
// Tailwind Class Tokens
// ============================================

// Text Colors
export const textColors = {
	primary: "text-[#111928]", // Main headings, important text
	secondary: "text-[#6B7280]", // Descriptions, secondary text
	tertiary: "text-[#374151]", // Less prominent text
	muted: "text-[#4B5563]", // Muted content
	dark: "text-[#1F2A37]", // Dark text
	link: "text-[#3F83F8]", // Links, interactive text
	error: "text-[#DC2626]", // Error states
	success: "text-[#0E9F6E]", // Success states
	white: "text-white",
} as const;

// Background Colors
export const bgColors = {
	primary: "bg-[#3F83F8]", // Primary buttons
	primaryHover: "hover:bg-[#3F83F8]/90",
	section: "bg-[#F9FAFB]", // Section backgrounds
	white: "bg-white",
	avatar: "bg-[#F97316]", // Avatar fallback
	success: "bg-[#0E9F6E]", // Success badges
	successLight: "bg-[#DEF7EC]",
	errorLight: "bg-[#FDE8E8]",
	warningLight: "bg-[#FEECDC]",
	infoLight: "bg-[#E1EFFE]",
	neutralLight: "bg-[#F3F4F6]",
	overlay: "bg-black/30",
	overlayBlur: "backdrop-blur-[4px]",
	cardBlur: "bg-white/95 backdrop-blur-[10px]",
	hoverLight: "hover:bg-[#F9FAFB]",
} as const;

// Border Colors
export const borderColors = {
	default: "border-[#E5E7EB]", // Default borders
	light: "border-[#D1D5DB]",
	primary: "border-[#3F83F8]", // Primary accent borders
	white: "border-white",
} as const;

// Divider
export const dividerColors = {
	default: "bg-[#E5E7EB]", // Horizontal dividers
	light: "bg-[#D1D5DB]",
} as const;

// ============================================
// Component-Specific Styles
// ============================================

// Button Variants
export const buttonStyles = {
	primary: "bg-[#3F83F8] hover:bg-[#3F83F8]/90 text-white",
	primaryFull: "w-full bg-[#3F83F8] hover:bg-[#3F83F8]/90 text-white",
	secondary:
		"border border-[#E5E7EB] text-[#111928] hover:bg-gray-50 bg-transparent",
	secondaryBlue:
		"border border-[#3F83F8] text-[#3F83F8] hover:bg-[#3F83F8]/5 bg-transparent",
	ghost: "hover:bg-[#F9FAFB]",
	link: "text-[#3F83F8] hover:underline",
	coral: "bg-[#EF4565] hover:bg-[#D93D5A] text-white",
	coralFull: "w-full bg-[#EF4565] hover:bg-[#D93D5A] text-white",
} as const;

// Avatar Styles
export const avatarStyles = {
	orange: "bg-[#F97316] text-white",
	purple: "bg-purple-100 text-purple-600",
	blue: "bg-blue-100 text-blue-600",
} as const;

// Card Styles
export const cardStyles = {
	default: "bg-white border border-[#E5E7EB] rounded-lg",
	hover: "hover:bg-[#F9FAFB] transition-colors",
	blur: "bg-white/95 backdrop-blur-[10px] rounded-xl",
} as const;

// Badge Styles
export const badgeStyles = {
	success: "bg-[#0E9F6E] text-white",
	error: "bg-[#F05252] text-white",
	warning: "bg-[#F97316] text-white",
	info: "bg-[#3F83F8] text-white",
	neutral: "bg-[#F3F4F6] text-[#4B5563]",
	online:
		"bg-[#0E9F6E] text-white text-xs px-2.5 py-1 rounded-full font-medium",
} as const;

// Status Colors (for leads, commissions, campaigns)
export const statusStyles = {
	success: {
		bg: "bg-green-100",
		text: "text-green-600",
		bgHover: "hover:bg-green-100",
	},
	warning: {
		bg: "bg-orange-100",
		text: "text-orange-600",
		bgHover: "hover:bg-orange-100",
	},
	error: {
		bg: "bg-red-100",
		text: "text-red-600",
		bgHover: "hover:bg-red-100",
	},
	info: {
		bg: "bg-blue-100",
		text: "text-blue-600",
		bgHover: "hover:bg-blue-100",
	},
	neutral: {
		bg: "bg-gray-100",
		text: "text-gray-600",
		bgHover: "hover:bg-gray-100",
	},
} as const;

// Tab Styles
export const tabStyles = {
	active: "text-[#DC2626]",
	inactive: "text-[#6B7280] hover:text-[#111928]",
	indicator: "bg-[#DC2626]",
} as const;

// Icon Colors
export const iconColors = {
	primary: "text-[#3F83F8]",
	secondary: "text-[#6B7280]",
	muted: "text-[#9CA3AF]",
	dark: "text-[#111928]",
} as const;

// ============================================
// Legacy Exports (for backward compatibility)
// ============================================

export const partnerColors = {
	primary: {
		blue: {
			50: "bg-blue-50",
			100: "bg-blue-100",
			500: "bg-blue-500",
			600: "bg-blue-600",
			700: "bg-blue-700",
		},
		purple: {
			100: "bg-purple-100",
			600: "bg-purple-600",
		},
	},
	text: {
		blue: {
			600: "text-blue-600",
			700: "text-blue-700",
			800: "text-blue-800",
		},
		purple: {
			600: "text-purple-600",
			700: "text-purple-700",
		},
		gray: {
			500: "text-gray-500",
			600: "text-gray-600",
			700: "text-gray-700",
			900: "text-gray-900",
		},
		green: {
			600: "text-green-600",
		},
		red: {
			600: "text-red-600",
		},
		orange: {
			600: "text-orange-600",
		},
	},
	border: {
		blue: {
			500: "border-blue-500",
		},
		gray: {
			200: "border-gray-200",
			300: "border-gray-300",
		},
	},
	ring: {
		blue: {
			500: "ring-blue-500",
		},
	},
	hover: {
		blue: {
			700: "hover:bg-blue-700",
			800: "hover:text-blue-800",
		},
		gray: {
			50: "hover:bg-gray-50",
			900: "hover:text-gray-900",
		},
	},
	status: statusStyles,
} as const;

export const buttonVariants = {
	primary: "btn-fancy",
	primaryFull: "w-full btn-fancy",
	coral: "bg-[#EF4565] hover:bg-[#D93D5A] text-white",
	coralFull: "w-full bg-[#EF4565] hover:bg-[#D93D5A] text-white",
} as const;

export const avatarColors = {
	primary: "bg-purple-100 text-purple-700",
	secondary: "bg-sky-100 text-sky-700",
} as const;

export const focusStates = {
	input: `${partnerColors.border.blue[500]} ${partnerColors.ring.blue[500]}`,
} as const;

export const brandColors = {
	amberFlex: {
		amber: partnerColors.text.gray[900],
		flex: partnerColors.text.blue[600],
	},
} as const;

// ============================================
// Helper Functions
// ============================================

export const getAvatarColor = (index: number): string => {
	return index % 2 === 0 ? avatarColors.primary : avatarColors.secondary;
};

export const getStatusColor = (status: string) => {
	const statusMap: Record<
		string,
		(typeof statusStyles)[keyof typeof statusStyles]
	> = {
		// Lead statuses
		contacted: statusStyles.warning,
		"not-booked": statusStyles.error,
		"non-responsive": statusStyles.info,
		booked: statusStyles.success,
		engaged: statusStyles.success,
		cancelled: statusStyles.error,
		"follow-up-later": statusStyles.warning,
		"repeat-users": statusStyles.neutral,
		// Commission statuses
		paid: statusStyles.success,
		rejected: statusStyles.error,
		approved: statusStyles.info,
		"partially-paid": statusStyles.warning,
		// Campaign statuses
		active: statusStyles.success,
		paused: statusStyles.warning,
		draft: statusStyles.neutral,
		completed: statusStyles.info,
		// Default
		default: statusStyles.neutral,
	};

	return statusMap[status] || statusMap.default;
};
